import React, { useState, useEffect } from "react";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Upload,
  Plus,
} from "lucide-react";
import { useTradingHistoryStore } from "../store/tradingHistoryStore";
import { useBrokerStore } from "../store/brokerStore";
import BrokerLogo from "./BrokerLogo";
import toast from "react-hot-toast";
import { usePortfolioStore } from "../store/portfolioStore";
import { useThemeStore } from "../store/themeStore";
import { formatCurrency, formatDate, getSectorInfo } from "../lib/utils";
import Button from "./ui/Button";
import Select from "./ui/Select";
import SearchableSelect from "./ui/SearchableSelect";
import StockLogo from "./StockLogo";
import AddTransactionModal from "./AddTransactionModal";
import TradingViewModal from "./TradingViewModal";

const TradingReport: React.FC = () => {
  const {
    tradingHistory,
    pnlSummary,
    loadTradingHistory,
    loadPnLSummary,
    addTradingRecord,
    loadingHistory,
    loadingPnL,
  } = useTradingHistoryStore();

  const { brokers, loadBrokers } = useBrokerStore();

  const {
    displayCurrency,
    exchangeRate,
    portfolio,
    getCurrentPrice,
    addHolding,
    customPortfolios,
    assignHoldingToPortfolio,
    portfolioHoldings,
    loadCustomPortfolios,
    loadPortfolioHoldings,
    sectors,
    countries,
  } = usePortfolioStore();

  const { maskAmounts } = useThemeStore();
  const MASK = "••••••";
  const m = (v: string) => (maskAmounts ? MASK : v);
  const isLoading = loadingHistory || loadingPnL;

  // Country/Sector/Broker chip row — same style as the Portfolio page
  const renderStockTags = (symbol: string, sector?: string | null) => {
    const holding = portfolio.find((h) => h.symbol === symbol);
    const sectorInfo = sector ? getSectorInfo(sector, sectors) : null;
    const countryInfo = holding?.country
      ? countries.find((c) => c.key === holding.country)
      : null;
    // Fully-sold positions have their holding row deleted, so fall back to
    // the most recent trade's broker (holdings has no such fallback for country).
    const brokerKey =
      holding?.broker ||
      tradingHistory
        .filter((r) => r.symbol === symbol && r.broker)
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        )[0]?.broker;
    const brokerInfo = brokerKey
      ? brokers.find((b) => b.key === brokerKey)
      : null;

    if (!sectorInfo && !countryInfo && !brokerInfo) return null;

    return (
      <div className="flex items-center gap-1 flex-wrap">
        {countryInfo && (
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-medium ${countryInfo.bg_color} ${countryInfo.text_color}`}
            style={{ fontSize: "9px" }}
          >
            <span>{countryInfo.icon}</span>
            <span className="truncate">{countryInfo.label}</span>
          </span>
        )}
        {sectorInfo && (
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-medium ${sectorInfo.bgColor} ${sectorInfo.textColor}`}
            style={{ fontSize: "9px" }}
          >
            <span>{sectorInfo.icon}</span>
            <span className="truncate">{sectorInfo.label}</span>
          </span>
        )}
        {brokerInfo && (
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-medium ${brokerInfo.bg_color} ${brokerInfo.text_color}`}
            style={{ fontSize: "9px" }}
          >
            <BrokerLogo
              brokerKey={brokerInfo.key}
              fallbackIcon={brokerInfo.icon}
              size={14}
            />
            <span className="truncate">{brokerInfo.label}</span>
          </span>
        )}
      </div>
    );
  };

  const [tvSymbol, setTvSymbol] = useState('');
  const [tvOpen, setTvOpen] = useState(false);
  const openTV = (symbol: string, currency?: string) => {
    setTvSymbol(currency === 'THB' ? `SET:${symbol}` : symbol);
    setTvOpen(true);
  };

  const [selectedPeriod, setSelectedPeriod] = useState<
    "all" | "1y" | "6m" | "3m" | "1m"
  >("all");
  const [selectedSymbol, setSelectedSymbol] = useState<string>("all");
  const [activeTab, setActiveTab] = useState<"holding" | "sold" | "history">(
    "holding",
  );
  const [showAddTransactionModal, setShowAddTransactionModal] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([loadTradingHistory(), loadPnLSummary(), loadBrokers()]);

      // Load custom portfolios first
      await loadCustomPortfolios();

      // Then load holdings for all portfolios
      const portfolios = usePortfolioStore.getState().customPortfolios;
      await Promise.all(
        portfolios.map((portfolio) => loadPortfolioHoldings(portfolio.id)),
      );
    };

    loadData();
  }, []);

  const periodOptions = [
    { value: "all", label: "All Time" },
    { value: "1y", label: "Last Year" },
    { value: "6m", label: "Last 6 Months" },
    { value: "3m", label: "Last 3 Months" },
    { value: "1m", label: "Last Month" },
  ];

  const symbolOptions = [
    { value: "all", label: "All Stocks" },
    ...Array.from(new Set((tradingHistory || []).map((t) => t.symbol))).map(
      (symbol) => ({
        value: symbol,
        label: symbol,
      }),
    ),
  ];

  const filteredHistory = (tradingHistory || []).filter((record) => {
    if (selectedSymbol !== "all" && record.symbol !== selectedSymbol)
      return false;

    if (selectedPeriod !== "all") {
      const days =
        { "1m": 30, "3m": 90, "6m": 180, "1y": 365 }[selectedPeriod] || 365;
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);
      if (new Date(record.created_at) < cutoffDate) return false;
    }

    return true;
  });

  // Helper function to convert amount to display currency
  const convertToDisplayCurrency = (amount: number, recordCurrency: string) => {
    // Handle null/undefined/NaN amounts
    if (!amount || isNaN(amount) || !isFinite(amount)) {
      return 0;
    }

    // Handle missing currency or exchange rate
    if (
      !recordCurrency ||
      !exchangeRate ||
      isNaN(exchangeRate) ||
      !isFinite(exchangeRate)
    ) {
      return amount;
    }

    if (recordCurrency === displayCurrency) {
      return amount;
    }

    if (displayCurrency === "USD" && recordCurrency === "THB") {
      return amount / exchangeRate;
    } else if (displayCurrency === "THB" && recordCurrency === "USD") {
      return amount * exchangeRate;
    }

    return amount;
  };

  const totalStats = (filteredHistory || []).reduce(
    (acc, record) => {
      const convertedAmount = convertToDisplayCurrency(
        record.total_amount,
        record.currency,
      );
      const shares = record.shares || 0;

      // Fix fees calculation - ensure we handle all fee values properly
      let convertedFees = 0;
      if (record.fees && record.fees > 0) {
        convertedFees = convertToDisplayCurrency(record.fees, record.currency);

        // Debug logging - remove this in production
        if (process.env.NODE_ENV === "development") {
          console.log(
            `Processing fees for ${record.symbol} ${record.action}: Original=${record.fees}, Converted=${convertedFees}, Currency=${record.currency}`,
          );
        }
      }

      // Only process if we have valid numbers
      if (isNaN(convertedAmount) || isNaN(shares)) {
        return acc;
      }

      // Add fees to total for both BUY and SELL transactions
      if (
        convertedFees > 0 &&
        !isNaN(convertedFees) &&
        isFinite(convertedFees)
      ) {
        acc.totalFees += convertedFees;
      }

      if (record.action === "BUY") {
        acc.totalBought += convertedAmount;
        acc.totalShares += shares;
      } else if (record.action === "SELL") {
        acc.totalSold += convertedAmount;
        acc.soldShares += shares;
      }

      return acc;
    },
    {
      totalBought: 0,
      totalSold: 0,
      totalShares: 0,
      soldShares: 0,
      totalFees: 0,
      realizedPnL: 0,
    },
  );

  // Debug logging for total stats
  if (process.env.NODE_ENV === "development") {
    console.log("Total Stats:", totalStats);
    console.log("Filtered History Length:", filteredHistory.length);
    console.log(
      "Sample records with fees:",
      filteredHistory
        .filter((r) => r.fees)
        .slice(0, 3)
        .map((r) => ({ symbol: r.symbol, fees: r.fees, currency: r.currency })),
    );
  }

  // Calculate proper realized P&L based on sell transactions
  const realizedPnL = (filteredHistory || [])
    .filter((record) => record.action === "SELL")
    .reduce((total, sellRecord) => {
      // For now, we'll use a simplified calculation
      // In a real scenario, you'd want to track cost basis per share sold
      const sellAmount = convertToDisplayCurrency(
        sellRecord.total_amount,
        sellRecord.currency,
      );
      const sellFees = sellRecord.fees
        ? convertToDisplayCurrency(sellRecord.fees, sellRecord.currency)
        : 0;

      // Find corresponding buy records for this symbol to calculate cost basis
      const buyRecords = (filteredHistory || []).filter(
        (r) =>
          r.action === "BUY" &&
          r.symbol === sellRecord.symbol &&
          new Date(r.created_at) <= new Date(sellRecord.created_at),
      );

      if (buyRecords.length > 0) {
        // Use weighted average cost
        const totalBoughtShares = buyRecords.reduce((sum, r) => {
          const shares = r.shares || 0;
          return sum + (isNaN(shares) ? 0 : shares);
        }, 0);
        const totalBoughtAmount = buyRecords.reduce((sum, r) => {
          const amount = convertToDisplayCurrency(r.total_amount, r.currency);
          return sum + (isNaN(amount) ? 0 : amount);
        }, 0);

        // Prevent division by zero
        if (totalBoughtShares <= 0) {
          return total;
        }

        const avgCostPerShare = totalBoughtAmount / totalBoughtShares;
        const sellShares = sellRecord.shares || 0;

        // Check for valid numbers
        if (isNaN(avgCostPerShare) || isNaN(sellShares) || isNaN(sellAmount)) {
          return total;
        }

        const costBasis = sellShares * avgCostPerShare;
        const profit = sellAmount - sellFees - costBasis;

        // Only add if profit is a valid number
        if (isNaN(profit) || !isFinite(profit)) {
          return total;
        }

        return total + profit;
      }

      return total;
    }, 0);

  // Calculate Unrealized P&L from current holdings
  const filteredPortfolio = portfolio || [];
  const unrealizedPnL = (filteredPortfolio || []).reduce((total, holding) => {
    const currentPrice = getCurrentPrice(holding.symbol, displayCurrency);
    const shares = holding.shares || 0;
    const purchasePrice = holding.purchase_price || 0;

    // Check for valid numbers
    if (
      !currentPrice ||
      currentPrice <= 0 ||
      isNaN(currentPrice) ||
      isNaN(shares) ||
      isNaN(purchasePrice) ||
      shares <= 0
    ) {
      return total;
    }

    const currentValue = shares * currentPrice;
    const costBasis = convertToDisplayCurrency(
      shares * purchasePrice,
      holding.purchase_currency,
    );

    const pnl = currentValue - costBasis;

    // Only add if pnl is a valid number
    if (isNaN(pnl) || !isFinite(pnl)) {
      return total;
    }

    return total + pnl;
  }, 0);

  const exportToCSV = () => {
    const headers = [
      "Date",
      "Symbol",
      "Action",
      "Shares",
      "Price",
      "Currency",
      "Total",
      "Fees",
      "Notes",
    ];
    const rows = (filteredHistory || []).map((record) => [
      formatDate(record.created_at),
      record.symbol,
      record.action,
      record.shares,
      record.price,
      record.currency,
      record.total_amount,
      record.fees || 0,
      record.notes || "",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trading-report-${selectedPeriod}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Parse CSV and import transactions
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }

    result.push(current.trim());
    return result;
  };

  const parseTransactionCSV = (csvText: string) => {
    const lines = csvText.split("\n").filter((line) => line.trim());
    const transactions: any[] = [];
    const stockSummary: {
      [symbol: string]: {
        bought: number;
        sold: number;
        avgPrice: number;
        totalCost: number;
      };
    } = {};

    // Skip header lines (first 3 lines based on your CSV format)
    for (let i = 3; i < lines.length; i++) {
      const fields = parseCSVLine(lines[i]);

      // Skip empty lines
      if (!fields[0] || fields[0].trim() === "") continue;

      const [
        symbol,
        ,
        broker,
        ,
        buyDate,
        buyQty,
        buyPrice,
        buyFees,
        buyValue,
        sellDate,
        sellQty,
        sellPrice,
        sellFees,
        sellValue,
      ] = fields;

      // Clean up values
      const cleanNumber = (str: string) => {
        if (!str) return 0;
        return parseFloat(str.replace(/[$,"\s]/g, "")) || 0;
      };

      const normalizeTradeDate = (value?: string | null) => {
        if (!value) return new Date().toISOString();
        const trimmed = value.trim();
        if (!trimmed) return new Date().toISOString();
        if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
          return `${trimmed.slice(0, 10)}T00:00:00.000Z`;
        }
        const dmyMatch = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
        if (dmyMatch) {
          const [, day, month, year] = dmyMatch;
          return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}T00:00:00.000Z`;
        }
        const parsed = new Date(trimmed);
        return Number.isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
      };

      const symbolTrimmed = symbol.trim();

      // Initialize stock summary if not exists
      if (!stockSummary[symbolTrimmed]) {
        stockSummary[symbolTrimmed] = {
          bought: 0,
          sold: 0,
          avgPrice: 0,
          totalCost: 0,
        };
      }

      // Add buy transaction if data exists
      if (buyQty && buyPrice && buyValue) {
        const shares = cleanNumber(buyQty);
        const price = cleanNumber(buyPrice);
        const totalAmount = cleanNumber(buyValue);

        transactions.push({
          symbol: symbolTrimmed,
          action: "BUY",
          shares,
          price,
          total_amount: totalAmount,
          fees: cleanNumber(buyFees),
          currency: "USD",
          port_api_type: "us_stock",
          created_at: normalizeTradeDate(buyDate),
          notes: `Imported from CSV - ${broker}`,
        });

        // Update stock summary for remaining shares calculation
        stockSummary[symbolTrimmed].bought += shares;
        stockSummary[symbolTrimmed].totalCost += totalAmount;
      }

      // Add sell transaction if data exists
      if (sellQty && sellPrice && sellValue) {
        const shares = cleanNumber(sellQty);

        transactions.push({
          symbol: symbolTrimmed,
          action: "SELL",
          shares,
          price: cleanNumber(sellPrice),
          total_amount: cleanNumber(sellValue),
          fees: cleanNumber(sellFees),
          currency: "USD",
          port_api_type: "us_stock",
          created_at: normalizeTradeDate(sellDate),
          notes: `Imported from CSV - ${broker}`,
        });

        // Update stock summary for remaining shares calculation
        stockSummary[symbolTrimmed].sold += shares;
      }
    }

    // Calculate average price for remaining shares
    Object.keys(stockSummary).forEach((symbol) => {
      const summary = stockSummary[symbol];
      if (summary.bought > 0) {
        summary.avgPrice = summary.totalCost / summary.bought;
      }
    });

    return { transactions, stockSummary };
  };

  const handleFileImport = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const csvText = e.target?.result as string;
        const { transactions, stockSummary } = parseTransactionCSV(csvText);

        if (transactions.length === 0) {
          toast.error("No valid transactions found in the CSV file.");
          return;
        }

        // Show confirmation before importing
        const confirmed = window.confirm(
          `Found ${transactions.length} transactions. Do you want to import them?\n\n` +
            "This will add the transactions to your trading history and remaining shares to your portfolio.",
        );

        if (!confirmed) return;

        // Find US Stocks portfolio (use the first USD portfolio)
        const usPortfolio =
          customPortfolios.find((p) => p.currency === "USD") ||
          customPortfolios[0];

        if (!usPortfolio) {
          toast.error("No portfolios found. Please create a portfolio first.");
          return;
        }

        // Import transactions one by one
        toast.loading("Importing transactions...", { id: "import" });

        let successCount = 0;
        let errorCount = 0;

        for (const transaction of transactions) {
          try {
            await addTradingRecord({
              symbol: transaction.symbol,
              name: transaction.symbol, // We don't have company names in CSV
              port_api_type: transaction.port_api_type,
              action: transaction.action,
              shares: transaction.shares,
              price: transaction.price,
              currency: transaction.currency,
              total_amount: transaction.total_amount,
              fees: transaction.fees,
              notes: transaction.notes,
              created_at: transaction.created_at,
              portfolio_id: usPortfolio.id,
            });
            successCount++;
          } catch (error) {
            console.error("Error importing transaction:", error);
            errorCount++;
          }
        }

        // Add remaining shares to หุ้นสหรัฐ portfolio
        let portfolioAddCount = 0;
        for (const [symbol, summary] of Object.entries(stockSummary)) {
          const remainingShares = summary.bought - summary.sold;
          if (remainingShares > 0) {
            try {
              // Add holding to general portfolio first
              const response = await addHolding({
                symbol,
                name: symbol, // We don't have company names in CSV
                port_api_type: "us_stock",
                shares: remainingShares,
                purchase_price: summary.avgPrice,
                purchase_currency: "USD",
                recordTradingHistory: false,
              });

              // Assign to หุ้นสหรัฐ portfolio
              if (response?.id) {
                await assignHoldingToPortfolio(usPortfolio.id, response.id);
              }

              portfolioAddCount++;
            } catch (error) {
              console.error(
                `Error adding ${symbol} to หุ้นสหรัฐ portfolio:`,
                error,
              );
            }
          }
        }

        toast.dismiss("import");

        if (successCount > 0) {
          let message = `Successfully imported ${successCount} transactions!`;
          if (portfolioAddCount > 0) {
            message += ` Added ${portfolioAddCount} stocks to "หุ้นสหรัฐ" portfolio.`;
          }
          toast.success(message);
          // Reload trading history to show new data
          await loadTradingHistory();
          await loadPnLSummary();
        }

        if (errorCount > 0) {
          toast.error(`Failed to import ${errorCount} transactions.`);
        }
      } catch (error) {
        console.error("Error parsing CSV:", error);
        toast.error("Error parsing CSV file. Please check the format.");
      }
    };

    reader.readAsText(file);
    // Reset the input
    event.target.value = "";
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Trading Report
        </h2>
        <div className="flex items-center gap-3">
          <input
            type="file"
            accept=".csv"
            onChange={handleFileImport}
            style={{ display: "none" }}
            id="csv-import"
          />
          {/* <Button variant="secondary" size="sm"
            onClick={() => document.getElementById('csv-import')?.click()}
          >
            <Download className="w-4 h-4" />
            Import CSV
          </Button> */}
          <Button size="sm"
            onClick={() => setShowAddTransactionModal(true)}
          >
            <Plus className="w-4 h-4" />
            <span className="font-semibold">Add Transaction</span>
          </Button>
          <Button variant="secondary" size="sm"
            onClick={exportToCSV}
            disabled={(filteredHistory || []).length === 0}
          >
            <Upload className="w-4 h-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Period
          </label>
          <Select
            value={selectedPeriod}
            options={periodOptions}
            onChange={(value) => setSelectedPeriod(value as any)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Symbol
          </label>
          <SearchableSelect
            value={selectedSymbol}
            options={symbolOptions}
            onChange={setSelectedSymbol}
          />
        </div>
        <div className="flex items-end">
          <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1 w-full">
            <button
              onClick={() => setActiveTab("holding")}
              className={`inline-flex items-center justify-center px-3 h-7 text-sm font-medium rounded-md transition-colors flex-1 ${
                activeTab === "holding"
                  ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              }`}
            >
              Holding
            </button>
            <button
              onClick={() => setActiveTab("sold")}
              className={`inline-flex items-center justify-center px-3 h-7 text-sm font-medium rounded-md transition-colors flex-1 ${
                activeTab === "sold"
                  ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              }`}
            >
              Sold
            </button>
            <button
              onClick={() => setActiveTab("history")}
              className={`inline-flex items-center justify-center px-3 h-7 text-sm font-medium rounded-md transition-colors flex-1 ${
                activeTab === "history"
                  ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              }`}
            >
              History
            </button>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {/* Volume */}
        <div className="stat-tile stat-tile-primary">
          <p className="stat-title">
            Volume
          </p>
          {isLoading ? (
            <>
              <div className="h-7 w-28 bg-white/60 dark:bg-white/10 rounded-lg animate-pulse" />
              <div className="h-3 w-20 bg-white/60 dark:bg-white/10 rounded animate-pulse" />
            </>
          ) : (
            <>
              <p className="stat-value">
                {m(formatCurrency(totalStats.totalBought, displayCurrency))}
              </p>
              <p className="stat-caption">
                {m(
                  displayCurrency === "THB"
                    ? `≈ ${formatCurrency(totalStats.totalBought / exchangeRate, "USD")}`
                    : `≈ ${formatCurrency(totalStats.totalBought * exchangeRate, "THB")}`,
                )}
              </p>
            </>
          )}
        </div>

        {/* Unrealized P&L */}
        <div
          className={`stat-tile ${unrealizedPnL >= 0 ? "stat-tile-gain" : "stat-tile-loss"}`}
        >
          <p
            className="stat-title"
          >
            Unrealized P&L
          </p>
          {isLoading ? (
            <>
              <div className="h-7 w-28 bg-white/60 dark:bg-white/10 rounded-lg animate-pulse" />
              <div className="h-3 w-20 bg-white/60 dark:bg-white/10 rounded animate-pulse" />
            </>
          ) : (
            <>
              <p
                className="stat-value"
              >
                {m(
                  `${unrealizedPnL >= 0 ? "+" : ""}${formatCurrency(unrealizedPnL, displayCurrency)}`,
                )}
              </p>
              <p
                className="stat-caption"
              >
                {m(
                  displayCurrency === "THB"
                    ? `≈ ${formatCurrency(unrealizedPnL / exchangeRate, "USD")}`
                    : `≈ ${formatCurrency(unrealizedPnL * exchangeRate, "THB")}`,
                )}
              </p>
            </>
          )}
        </div>

        {/* Realized P&L */}
        <div
          className={`stat-tile ${realizedPnL >= 0 ? "stat-tile-gain" : "stat-tile-loss"}`}
        >
          <p
            className="stat-title"
          >
            Realized P&L
          </p>
          {isLoading ? (
            <>
              <div className="h-7 w-28 bg-white/60 dark:bg-white/10 rounded-lg animate-pulse" />
              <div className="h-3 w-20 bg-white/60 dark:bg-white/10 rounded animate-pulse" />
            </>
          ) : (
            <>
              <p
                className="stat-value"
              >
                {m(
                  `${realizedPnL >= 0 ? "+" : ""}${formatCurrency(realizedPnL, displayCurrency)}`,
                )}
              </p>
              <p
                className="stat-caption"
              >
                {m(
                  displayCurrency === "THB"
                    ? `≈ ${formatCurrency(realizedPnL / exchangeRate, "USD")}`
                    : `≈ ${formatCurrency(realizedPnL * exchangeRate, "THB")}`,
                )}
              </p>
            </>
          )}
        </div>

        {/* Total Fees */}
        <div className="stat-tile stat-tile-violet">
          <p className="stat-title">
            Total Fees
          </p>
          {isLoading ? (
            <>
              <div className="h-7 w-24 bg-white/60 dark:bg-white/10 rounded-lg animate-pulse" />
              <div className="h-3 w-16 bg-white/60 dark:bg-white/10 rounded animate-pulse" />
            </>
          ) : (
            <>
              <p className="stat-value">
                {m(formatCurrency(totalStats.totalFees, displayCurrency))}
              </p>
              <p className="stat-caption">
                {m(
                  displayCurrency === "THB"
                    ? `≈ ${formatCurrency(totalStats.totalFees / exchangeRate, "USD")}`
                    : `≈ ${formatCurrency(totalStats.totalFees * exchangeRate, "THB")}`,
                )}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Holding tab */}
      {activeTab === "holding" && (
        <div className="space-y-6">
          {loadingPnL ? (
            <div>
              <div className="h-5 w-52 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-4" />
              <div className="table-bleed">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/20 dark:via-indigo-900/20 dark:to-purple-900/20 border-y border-indigo-100 dark:border-gray-700">
                    <tr>
                      {["18%","12%","12%","12%","14%","14%","16%"].map((w, i) => (
                        <th key={i} className="py-4 px-3" style={{ width: w }}>
                          <div className={`h-3 bg-primary-200/50 dark:bg-primary-800/30 rounded animate-pulse ${i === 0 ? "w-24" : "w-14 ml-auto"}`} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {[72, 60, 80, 55, 68, 75].map((w, i) => (
                      <tr key={i}>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse flex-shrink-0" />
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" style={{ width: `${w}px` }} />
                          </div>
                        </td>
                        {[50, 56, 56, 64, 64, 64].map((cw, j) => (
                          <td key={j} className="py-3 px-3 text-right">
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse ml-auto" style={{ width: `${cw - (i % 3) * 4}px` }} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
          (() => {
            const holdingStocks = (pnlSummary || []).filter((summary) => {
              if (selectedSymbol !== "all" && summary.symbol !== selectedSymbol)
                return false;
              return summary.remaining_shares > 0;
            });

            if (holdingStocks.length === 0) return null;

            // Create symbol to portfolio name mapping
            const symbolToPortfolio: Record<
              string,
              { name: string; icon: string }
            > = {};

            Object.entries(portfolioHoldings).forEach(
              ([portfolioId, holdings]) => {
                const portfolioData = customPortfolios.find(
                  (p) => p.id === Number(portfolioId),
                );
                if (portfolioData) {
                  holdings.forEach((holding) => {
                    symbolToPortfolio[holding.symbol] = {
                      name: portfolioData.name,
                      icon: portfolioData.icon || "📊",
                    };
                  });
                }
              },
            );

            // Group by portfolio - only include stocks that are assigned to a portfolio
            const groupedByPortfolio = holdingStocks.reduce(
              (groups, summary) => {
                const portfolioInfo = symbolToPortfolio[summary.symbol];

                // Skip stocks that are not assigned to any portfolio
                if (!portfolioInfo?.name) return groups;

                const portfolioName = portfolioInfo.name;

                if (!groups[portfolioName]) {
                  groups[portfolioName] = {
                    icon: portfolioInfo.icon,
                    stocks: [],
                  };
                }
                groups[portfolioName].stocks.push(summary);
                return groups;
              },
              {} as Record<
                string,
                { icon: string; stocks: typeof holdingStocks }
              >,
            );

            // Sort stocks within each portfolio by allocation percentage
            Object.values(groupedByPortfolio).forEach((group) => {
              // Calculate total portfolio value
              const totalPortfolioValue = group.stocks.reduce((sum, stock) => {
                const value = stock.current_price
                  ? stock.remaining_shares * stock.current_price
                  : stock.total_cost;
                return sum + value;
              }, 0);

              // Sort by allocation percentage (descending)
              group.stocks.sort((a, b) => {
                const valueA = a.current_price
                  ? a.remaining_shares * a.current_price
                  : a.total_cost;
                const valueB = b.current_price
                  ? b.remaining_shares * b.current_price
                  : b.total_cost;

                const percentA =
                  totalPortfolioValue > 0
                    ? (valueA / totalPortfolioValue) * 100
                    : 0;
                const percentB =
                  totalPortfolioValue > 0
                    ? (valueB / totalPortfolioValue) * 100
                    : 0;

                // Sort descending (highest percentage first)
                return percentB - percentA;
              });
            });

            const portfolioNames = Object.keys(groupedByPortfolio).sort();

            // Calculate total count of assigned stocks only
            const totalAssignedStocks = Object.values(
              groupedByPortfolio,
            ).reduce((sum, group) => sum + group.stocks.length, 0);

            return (
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  Currently Holding ({totalAssignedStocks})
                </h3>

                {/* Portfolio Groups */}
                <div className="space-y-6">
                  {portfolioNames.map((portfolioName) => {
                    const { icon, stocks } = groupedByPortfolio[portfolioName];

                    return (
                      <div key={portfolioName}>
                        <div className="table-bleed">
                          <table className="w-full">
                            <thead className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/20 dark:via-indigo-900/20 dark:to-purple-900/20 border-y border-indigo-100 dark:border-gray-700">
                              <tr>
                                <th className="text-left py-4 px-3 text-sm font-semibold text-gray-700 dark:text-gray-200 leading-none w-[26%]">
                                  <span className="flex items-center gap-1.5">
                                    <span>{icon}</span>
                                    <span>{portfolioName}</span>
                                    <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-primary-100 dark:bg-primary-900/50 text-primary-500 dark:text-primary-400 tracking-normal">
                                      {stocks.length} กองทุน
                                    </span>
                                  </span>
                                </th>
                                <th className="text-right py-4 px-3 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none w-[10%]">
                                  Shares
                                </th>
                                <th className="text-right py-4 px-3 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none w-[10%]">
                                  Avg Cost
                                </th>
                                <th className="text-right py-4 px-3 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none w-[10%]">
                                  Current Price
                                </th>
                                <th className="text-right py-4 px-3 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none w-[14%]">
                                  Cost Basis
                                </th>
                                <th className="text-right py-4 px-3 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none w-[14%]">
                                  Current Value
                                </th>
                                <th className="text-right py-4 px-3 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none w-[16%]">
                                  Unrealized P&L
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                              {stocks.map((summary, index) => {
                                const currentPrice =
                                  summary.current_price ||
                                  getCurrentPrice(
                                    summary.symbol,
                                    summary.currency,
                                  );
                                const currentValue =
                                  currentPrice > 0
                                    ? summary.remaining_shares * currentPrice
                                    : 0;
                                const costOfRemainingShares =
                                  summary.total_bought_shares > 0
                                    ? (summary.total_cost /
                                        summary.total_bought_shares) *
                                      summary.remaining_shares
                                    : 0;
                                const unrealizedPnL =
                                  summary.unrealized_pnl ||
                                  currentValue - costOfRemainingShares;
                                const pnlPercent =
                                  costOfRemainingShares > 0
                                    ? (
                                        (unrealizedPnL /
                                          costOfRemainingShares) *
                                        100
                                      ).toFixed(2)
                                    : "0.00";
                                const avgCost =
                                  summary.total_bought_shares > 0
                                    ? summary.total_cost /
                                      summary.total_bought_shares
                                    : 0;
                                // const priceChange = avgCost > 0 ? ((currentPrice - avgCost) / avgCost) * 100 : 0
                                // const priceChangePercent = priceChange.toFixed(2)

                                return (
                                  <tr
                                    key={`holding-${summary.symbol}-${index}`}
                                    className="hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all duration-200"
                                  >
                                    <td className="py-3 px-3">
                                      <button
                                        onClick={() => openTV(summary.symbol, summary.currency || displayCurrency)}
                                        className="flex items-center gap-2 group/tv hover:opacity-80 transition-opacity text-left"
                                      >
                                        <StockLogo
                                          symbol={summary.symbol}
                                          size={24}
                                          currency={
                                            summary.currency || displayCurrency
                                          }
                                          className="flex-shrink-0"
                                        />
                                        <div className="min-w-0 flex flex-col gap-0">
                                          <div className="flex items-center gap-2 min-w-0">
                                            <span className="text-sm font-medium text-gray-900 dark:text-white flex-shrink-0 group-hover/tv:text-blue-600 dark:group-hover/tv:text-blue-400 transition-colors">
                                              {summary.symbol}
                                            </span>
                                          </div>
                                          {renderStockTags(summary.symbol, summary.sector)}
                                        </div>
                                      </button>
                                    </td>
                                    <td className="py-3 px-3 text-right">
                                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                        {summary.remaining_shares.toLocaleString()}
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          /
                                          {summary.total_bought_shares.toLocaleString()}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="py-3 px-3 text-right">
                                      <div className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                        {m(
                                          formatCurrency(
                                            convertToDisplayCurrency(
                                              avgCost,
                                              summary.currency,
                                            ),
                                            displayCurrency,
                                          ),
                                        )}
                                      </div>
                                    </td>
                                    <td className="py-3 px-3 text-right">
                                      {currentPrice > 0 ? (
                                        <div>
                                          <div className="text-sm font-medium text-gray-600 dark:text-white">
                                            {m(
                                              formatCurrency(
                                                convertToDisplayCurrency(
                                                  currentPrice,
                                                  summary.currency,
                                                ),
                                                displayCurrency,
                                              ),
                                            )}
                                          </div>
                                          {/* {avgCost > 0 && (
                                    <div className={`flex items-center justify-end gap-1 text-xs font-semibold ${
                                      priceChange >= 0 ? 'text-gain' : 'text-loss'
                                    }`}>
                                      {priceChange >= 0 ? (
                                        <TrendingUp className="w-3 h-3" />
                                      ) : (
                                        <TrendingDown className="w-3 h-3" />
                                      )}
                                      <span>{priceChange >= 0 ? '+' : ''}{priceChangePercent}%</span>
                                    </div>
                                  )} */}
                                        </div>
                                      ) : (
                                        <span className="text-sm text-gray-400 dark:text-gray-400">
                                          -
                                        </span>
                                      )}
                                    </td>
                                    <td className="py-3 px-3 text-right bg-blue-50/30 dark:bg-blue-900/10">
                                      <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                                        {m(
                                          formatCurrency(
                                            convertToDisplayCurrency(
                                              costOfRemainingShares,
                                              summary.currency,
                                            ),
                                            displayCurrency,
                                          ),
                                        )}
                                      </div>
                                    </td>
                                    <td className="py-3 px-3 text-right bg-purple-50/30 dark:bg-purple-900/10">
                                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                        {m(
                                          formatCurrency(
                                            convertToDisplayCurrency(
                                              currentValue,
                                              summary.currency,
                                            ),
                                            displayCurrency,
                                          ),
                                        )}
                                      </div>
                                    </td>
                                    <td
                                      className={`py-3 px-3 text-right ${
                                        unrealizedPnL >= 0
                                          ? "bg-gain-soft/30"
                                          : "bg-loss-soft/30"
                                      }`}
                                    >
                                      <div
                                        className={`text-sm font-semibold ${
                                          unrealizedPnL >= 0
                                            ? "text-gain"
                                            : "text-loss"
                                        }`}
                                      >
                                        {m(
                                          `${unrealizedPnL >= 0 ? "+" : ""}${formatCurrency(convertToDisplayCurrency(unrealizedPnL, summary.currency), displayCurrency)}`,
                                        )}
                                      </div>
                                      <div
                                        className={`flex items-center justify-end gap-1 text-xs font-semibold ${
                                          unrealizedPnL >= 0
                                            ? "text-gain"
                                            : "text-loss"
                                        }`}
                                      >
                                        {unrealizedPnL >= 0 ? (
                                          <TrendingUp className="w-3 h-3" />
                                        ) : (
                                          <TrendingDown className="w-3 h-3" />
                                        )}
                                        <span>
                                          {unrealizedPnL >= 0 ? "+" : ""}
                                          {pnlPercent}%
                                        </span>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()
          )}
        </div>
      )}

      {/* Sold tab */}
      {activeTab === "sold" && (
        <div className="space-y-6">
          {loadingPnL ? (
            <div className="table-bleed">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/20 dark:via-indigo-900/20 dark:to-purple-900/20 border-y border-indigo-100 dark:border-gray-700">
                  <tr>
                    {["20%","13%","13%","18%","18%","18%"].map((w, i) => (
                      <th key={i} className="py-4 px-3" style={{ width: w }}>
                        <div className={`h-3 bg-primary-200/50 dark:bg-primary-800/30 rounded animate-pulse ${i === 0 ? "w-16" : "w-12 ml-auto"}`} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[68, 55, 78, 62, 70, 58].map((w, i) => (
                    <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 px-2">
                        <div className="flex items-center gap-1.5">
                          <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse flex-shrink-0" />
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" style={{ width: `${w}px` }} />
                        </div>
                      </td>
                      {[40, 52, 60, 60, 44].map((cw, j) => (
                        <td key={j} className="py-2 px-2 text-right">
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse ml-auto" style={{ width: `${cw - (i % 2) * 6}px` }} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
          (() => {
            const soldStocks = (pnlSummary || []).filter((summary) => {
              if (selectedSymbol !== "all" && summary.symbol !== selectedSymbol)
                return false;
              return (
                summary.remaining_shares === 0 && summary.total_sold_shares > 0
              );
            });

            if (soldStocks.length === 0) return null;

            // Group sold stocks by portfolio (using portfolio info from PnL summary)
            const groupedByPortfolio = soldStocks.reduce(
              (groups, summary) => {
                // Use portfolio info from the PnL summary directly
                const portfolioName =
                  summary.portfolio_name || "ไม่ได้กำหนดกลุ่ม";
                const portfolioIcon = summary.portfolio_icon || "📊";

                if (!groups[portfolioName]) {
                  groups[portfolioName] = {
                    icon: portfolioIcon,
                    stocks: [],
                  };
                }
                groups[portfolioName].stocks.push(summary);
                return groups;
              },
              {} as Record<string, { icon: string; stocks: typeof soldStocks }>,
            );

            const portfolioNames = Object.keys(groupedByPortfolio).sort();

            if (soldStocks.length === 0)
              return (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No fully sold stocks.</p>
                </div>
              );

            return (
              <div className="space-y-6">
                {portfolioNames.map((portfolioName) => {
                  const { icon, stocks } = groupedByPortfolio[portfolioName];

                  return (
                    <div key={portfolioName}>
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2 pl-4">
                        <span className="text-lg">{icon}</span>
                        <span>{portfolioName}</span>
                        <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                          ({stocks.length}{" "}
                          {stocks.length === 1 ? "stock" : "stocks"})
                        </span>
                      </h4>
                      <div className="table-bleed">
                        <table className="w-full">
                          <thead className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/20 dark:via-indigo-900/20 dark:to-purple-900/20 border-y border-indigo-100 dark:border-gray-700">
                            <tr>
                              <th className="text-left py-4 px-3 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none w-[28%]">
                                Stock
                              </th>
                              <th className="text-right py-4 px-3 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none w-[11%]">
                                Shares
                              </th>
                              <th className="text-right py-4 px-3 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none w-[11%]">
                                Avg Cost
                              </th>
                              <th className="text-right py-4 px-3 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none w-[16%]">
                                Total Cost
                              </th>
                              <th className="text-right py-4 px-3 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none w-[17%]">
                                Revenue
                              </th>
                              <th className="text-right py-4 px-3 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none w-[17%]">
                                Realized P&L
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {stocks.map((summary, index) => {
                              const pnlPercent =
                                summary.total_cost > 0
                                  ? (
                                      (summary.realized_pnl /
                                        summary.total_cost) *
                                      100
                                    ).toFixed(2)
                                  : "0.00";
                              const avgCost =
                                summary.total_bought_shares > 0
                                  ? summary.total_cost /
                                    summary.total_bought_shares
                                  : 0;

                              return (
                                <tr
                                  key={`sold-${summary.symbol}-${index}`}
                                  className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                                >
                                  <td className="py-2 px-2">
                                    <button
                                      onClick={() => openTV(summary.symbol, summary.currency || displayCurrency)}
                                      className="flex items-center gap-1.5 group/tv hover:opacity-80 transition-opacity text-left"
                                    >
                                      <StockLogo
                                        symbol={summary.symbol}
                                        size={20}
                                        currency={
                                          summary.currency || displayCurrency
                                        }
                                        className="flex-shrink-0"
                                      />
                                      <div className="min-w-0 flex flex-col gap-0">
                                        <div className="flex items-center gap-2 min-w-0">
                                          <span className="text-sm font-medium text-gray-900 dark:text-white flex-shrink-0 group-hover/tv:text-blue-600 dark:group-hover/tv:text-blue-400 transition-colors">
                                            {summary.symbol}
                                          </span>
                                          {summary.name &&
                                            summary.name !== summary.symbol && (
                                              <p className="text-xs text-gray-400 dark:text-gray-400 truncate leading-tight">
                                                {summary.name}
                                              </p>
                                            )}
                                        </div>
                                        {renderStockTags(summary.symbol, summary.sector)}
                                      </div>
                                    </button>
                                  </td>
                                  <td className="py-2 px-2 text-right">
                                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                      0
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      /
                                      {summary.total_bought_shares.toLocaleString()}
                                    </div>
                                  </td>
                                  <td className="py-2 px-2 text-right">
                                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                      {m(
                                        formatCurrency(
                                          convertToDisplayCurrency(
                                            avgCost,
                                            summary.currency,
                                          ),
                                          displayCurrency,
                                        ),
                                      )}
                                    </div>
                                  </td>
                                  <td className="py-2 px-2 text-right">
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                      {m(
                                        formatCurrency(
                                          convertToDisplayCurrency(
                                            summary.total_cost || 0,
                                            summary.currency,
                                          ),
                                          displayCurrency,
                                        ),
                                      )}
                                    </div>
                                  </td>
                                  <td className="py-2 px-2 text-right">
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                      {m(
                                        formatCurrency(
                                          convertToDisplayCurrency(
                                            summary.total_revenue || 0,
                                            summary.currency,
                                          ),
                                          displayCurrency,
                                        ),
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      {summary.total_sold_shares} sold
                                    </div>
                                  </td>
                                  <td className="py-2 px-2 text-right">
                                    <div
                                      className={`text-sm font-semibold ${summary.realized_pnl >= 0 ? "text-gain" : "text-loss"}`}
                                    >
                                      {m(
                                        `${summary.realized_pnl >= 0 ? "+" : ""}${formatCurrency(convertToDisplayCurrency(summary.realized_pnl, summary.currency), displayCurrency)}`,
                                      )}
                                    </div>
                                    <div
                                      className={`flex items-center justify-end gap-1 text-xs ${summary.realized_pnl >= 0 ? "text-gain" : "text-loss"}`}
                                    >
                                      {summary.realized_pnl >= 0 ? (
                                        <TrendingUp className="w-3 h-3" />
                                      ) : (
                                        <TrendingDown className="w-3 h-3" />
                                      )}
                                      <span>
                                        {summary.realized_pnl >= 0 ? "+" : ""}
                                        {pnlPercent}%
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()
          )}
        </div>
      )}

      {/* History tab */}
      {activeTab === "history" && (
        <div className="table-bleed">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/20 dark:via-indigo-900/20 dark:to-purple-900/20 border-y border-indigo-100 dark:border-gray-700">
              <tr>
                <th className="text-left py-4 px-3 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none w-[15%]">
                  Date
                </th>
                <th className="text-left py-4 px-3 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none w-[10%]">
                  Action
                </th>
                <th className="text-left py-4 px-3 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none w-[18%]">
                  Stock
                </th>
                <th className="text-left py-4 px-3 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none w-[13%]">
                  Broker
                </th>
                <th className="text-right py-4 px-3 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none w-[12%]">
                  Shares
                </th>
                <th className="text-right py-4 px-3 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none w-[12%]">
                  Price
                </th>
                <th className="text-right py-4 px-3 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none w-[12%]">
                  Total
                </th>
                <th className="text-right py-4 px-3 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none w-[10%]">
                  Fees
                </th>
              </tr>
            </thead>
            <tbody>
              {loadingHistory ? (
                [72, 58, 80, 65, 70, 55, 75, 62].map((w, i) => (
                  <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-2 px-2"><div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" /></td>
                    <td className="py-2 px-2"><div className="h-5 w-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" /></td>
                    <td className="py-2 px-2">
                      <div className="flex items-center gap-1.5">
                        <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse flex-shrink-0" />
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" style={{ width: `${w}px` }} />
                      </div>
                    </td>
                    <td className="py-2 px-2"><div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" /></td>
                    <td className="py-2 px-2 text-right"><div className="h-4 w-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulse ml-auto" /></td>
                    <td className="py-2 px-2 text-right"><div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse ml-auto" /></td>
                    <td className="py-2 px-2 text-right"><div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse ml-auto" /></td>
                    <td className="py-2 px-2 text-right"><div className="h-4 w-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse ml-auto" /></td>
                  </tr>
                ))
              ) : (filteredHistory || []).map((record, index) => {
                const totalAmount = convertToDisplayCurrency(
                  record.total_amount,
                  record.currency,
                );
                const pricePerShare = convertToDisplayCurrency(
                  record.price,
                  record.currency,
                );
                const fees = record.fees
                  ? convertToDisplayCurrency(record.fees, record.currency)
                  : 0;

                return (
                  <tr
                    key={`${record.id}-${index}`}
                    className={`border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${
                      record.action === "BUY"
                        ? "bg-blue-50/30 dark:bg-blue-900/5"
                        : "bg-orange-50/30 dark:bg-orange-900/5"
                    }`}
                  >
                    {/* Date */}
                    <td className="py-2 px-2">
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {formatDate(record.created_at)}
                      </div>
                    </td>

                    {/* Action */}
                    <td className="py-2 px-2">
                      <span
                        className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-bold ${
                          record.action === "BUY"
                            ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                            : "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300"
                        }`}
                      >
                        {record.action}
                      </span>
                    </td>

                    {/* Stock */}
                    <td className="py-2 px-2">
                      <button
                        onClick={() => openTV(record.symbol, record.currency || displayCurrency)}
                        className="flex items-center gap-1.5 group/tv hover:opacity-80 transition-opacity text-left"
                      >
                        <StockLogo
                          symbol={record.symbol}
                          size={20}
                          currency={record.currency || displayCurrency}
                          className="flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <span className="text-sm font-medium text-gray-900 dark:text-white group-hover/tv:text-blue-600 dark:group-hover/tv:text-blue-400 transition-colors">
                            {record.symbol}
                          </span>
                          {record.name && record.name !== record.symbol && (
                            <p className="text-xs text-gray-400 dark:text-gray-400 truncate leading-tight">
                              {record.name}
                            </p>
                          )}
                        </div>
                      </button>
                    </td>

                    {/* Broker */}
                    <td className="py-2 px-2">
                      {(() => {
                        const broker = brokers.find(
                          (b) => b.key === record.broker,
                        );
                        return broker ? (
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${broker.bg_color} ${broker.text_color}`}
                          >
                            <BrokerLogo
                              brokerKey={broker.key}
                              fallbackIcon={broker.icon}
                              size={14}
                            />
                            <span className="truncate max-w-[80px]">
                              {broker.label}
                            </span>
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-400">
                            -
                          </span>
                        );
                      })()}
                    </td>

                    {/* Shares */}
                    <td className="py-2 px-2 text-right">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {record.shares.toLocaleString()}
                      </div>
                    </td>

                    {/* Price */}
                    <td className="py-2 px-2 text-right">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {m(formatCurrency(pricePerShare, displayCurrency))}
                      </div>
                    </td>

                    {/* Total */}
                    <td className="py-2 px-2 text-right">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {m(formatCurrency(totalAmount, displayCurrency))}
                      </div>
                    </td>

                    {/* Fees */}
                    <td className="py-2 px-2 text-right">
                      {fees > 0 ? (
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {m(formatCurrency(fees, displayCurrency))}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-400 dark:text-gray-400">
                          -
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === "history" && !loadingHistory && (filteredHistory || []).length === 0 && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No trading history found for the selected filters.</p>
        </div>
      )}

      <AddTransactionModal
        isOpen={showAddTransactionModal}
        onClose={() => setShowAddTransactionModal(false)}
      />

      <TradingViewModal
        isOpen={tvOpen}
        onClose={() => setTvOpen(false)}
        symbol={tvSymbol}
      />
    </div>
  );
};

export default TradingReport;
